/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import { deriveField, reconstructTerm, negateTermType } from './luceneSelectors';
import { netParity, deriveTermType } from './luceneTransform';
import type { FilterType, FlatAst, FlatAsts } from '../../../types/luceneTypes';
const MERGEABLE_TYPES = new Set(['equals', 'not_equals']);
type Options = {
  terms: Array<string>;
  termMin: string | null | undefined;
  termMax: string | null | undefined;
  type: FilterType;
  field: string;
};

class QueryPartState {
  terms: Array<string>;

  termMin: string | null | undefined;

  termMax: string | null | undefined;

  type: FilterType;

  field: string;

  constructor(options: Options) {
    this.terms = options.terms;
    this.termMin = options.termMin;
    this.termMax = options.termMax;
    this.type = options.type;
    this.field = options.field;
  }

  static fromPart(part: FlatAst): QueryPartState {
    const { statements } = part;
    const { operators, groupPrefixes } = statements[0];
    const options = {};
    options.type = netParity(operators, groupPrefixes)
      ? deriveTermType(statements[0].ast)
      : negateTermType(deriveTermType(statements[0].ast));

    if (statements[0].ast.term || statements[0].ast.term === '') {
      options.terms = statements.map(
        (
          statement // $FlowFixMe type refinement not working in this case
        ) => statement.ast.term.replace(/\*/g, '')
      );
    } else {
      // $FlowFixMe type refinement not working in this case
      options.termMin = statements[0].ast.term_min;
      // $FlowFixMe type refinement not working in this case
      options.termMax = statements[0].ast.term_max;
    }

    options.field = deriveField(part);
    // $FlowFixMe type refinement not working in this case
    return new QueryPartState(options);
  }

  // combines and de-duplicates two arrays of terms
  static mergeTerms(terms1: Array<string>, terms2: Array<string>): Array<string> {
    return Array.from(new Set([...terms1, ...terms2]));
  }

  // this method will merge two QueryPartStates's terms and create a new QueryPartState
  static mergeQueryPartStates(queryPartState1: QueryPartState, queryPartState2: QueryPartState): QueryPartState {
    return new QueryPartState({
      terms: this.mergeTerms(queryPartState1.terms, queryPartState2.terms),
      type: queryPartState1.type,
      field: queryPartState1.field,
      termMin: undefined,
      termMax: undefined,
    });
  }

  // we consolidate query parts that check for exact matching on the same field,
  // because it never makes sense to have 'AND' separated equality subexpressions sharing
  // the same field.
  // e.g. '{field:foo, terms:[bar]} AND {field:foo, terms:[baz]}' will check for
  // foo being equal to both bar and baz at the same time, which is impossible and not useful
  static consolidateQueryPartStates(queryPartStates: Array<QueryPartState>): Array<QueryPartState> {
    const newQueryPartStates = [];
    const seenFieldAndTypes = {};
    queryPartStates.forEach((queryPartState) => {
      if (!MERGEABLE_TYPES.has(queryPartState.type)) {
        return newQueryPartStates.push(queryPartState);
      }

      const mergeKey = `${queryPartState.field}-${queryPartState.type}`;
      const prevIndex = seenFieldAndTypes[mergeKey];

      if (Number.isInteger(prevIndex)) {
        const prevQueryPartState = newQueryPartStates[prevIndex];
        // create a new queryPartState to avoid mutating previous objects
        const updatedQueryPartState = this.mergeQueryPartStates(prevQueryPartState, queryPartState);
        newQueryPartStates[prevIndex] = updatedQueryPartState;
        return;
      }

      newQueryPartStates.push(queryPartState);
      seenFieldAndTypes[mergeKey] = newQueryPartStates.length - 1;
    });
    return newQueryPartStates;
  }

  // Attempts to convert a collection fo queryPartStates into flatAsts.
  // Return undefined if any of the queryPartStates contain incomplete/illegal
  // syntax.
  static toFlatAsts(queryPartStates: Array<QueryPartState>): void | FlatAsts {
    const flatAsts = [];
    let hasIncompleteQueryPartState = false;
    queryPartStates.forEach((queryPartState) => {
      const flatAst = queryPartState.toFlatAst();

      if (!flatAst) {
        return (hasIncompleteQueryPartState = true);
      }

      flatAsts.push(flatAst);
    });

    if (hasIncompleteQueryPartState) {
      return undefined;
    }

    return flatAsts;
  }

  // Converting back to FlatAst as an intermediate step in converting queryPartState to
  // lucene string
  toFlatAst(): void | FlatAst {
    const { terms, termMin, termMax, type, field } = this;
    const operators = R.startsWith('not_', type) ? ['AND NOT'] : ['AND'];
    const groupPrefixes = [];

    if (!field) {
      // fields must be defined and cannot be the empty string
      return undefined;
    }

    if (type.includes('range')) {
      // ranges must have both terms
      if (!termMin || !termMax) {
        return undefined;
      }

      const statements = [
        {
          ast: {
            field,
            term_min: termMin,
            term_max: termMax,
            inclusive: 'both',
          },
          operators,
          groupPrefixes,
        },
      ];
      return {
        statements,
        explicitField: field,
      };
    }

    if (type.includes('exist')) {
      const statements = [
        {
          ast: {
            field,
            term: '*',
            quoted: false,
            prefix: null,
            similarity: null,
            boost: null,
          },
          operators,
          groupPrefixes,
        },
      ];
      return {
        statements,
        explicitField: field,
      };
    }

    if (!terms || !terms.length || (terms.length === 1 && terms[0] === '') || terms.every((term) => !term)) {
      // terms must be defined and cannot be only empty strings
      return undefined;
    }

    const statements = terms // empty strings are filtered out
      .filter((term) => !!term)
      .map((term, i) => ({
        ast: {
          field,
          term: reconstructTerm(term, type),
          // we automatically convert non-wildcard matching to quoted to support
          // phrases that include spaces. However, until our branch-lucene
          // parsing library supports wildcards outside of quoted values, these
          // types of asts will not be quoted.
          quoted: ['equals', 'not_equals'].includes(type),
          prefix: null,
          similarity: null,
          boost: null,
        },
        // make defaults constants
        operators: i ? operators.concat(['OR']) : operators,
        groupPrefixes,
      }));
    return {
      statements,
      explicitField: field,
    };
  }
}

export default QueryPartState;
